import { useCallback, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useLocation } from "react-router-dom";

import { Breadcrumbs } from "ui/breadcrumbs";
import ColumnOptions from "components/partials/column-options/column-options";
import { DataGrid, TableLoader, TableSearchAndFilters, TableContainer } from "ui/data-grid";

import { useGetBatchStatusesQuery } from "hooks/api/dictionary";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useBatches } from "hooks/api/use-batches";

import { createClientApprovalQueueColumns } from "containers/client/reviews/approval-queue/approval-queue-table-columns";
import {
  CLIENT_APPROVAL_QUEUE_SEARCH_PLACEHOLDER,
  CLIENT_TABLE_CONFIG_KEYS,
} from "containers/client/components/client-reviews-table/client-reviews-table.consts";
import { useClientTableColumnLayout } from "containers/client/hooks/use-client-table-column-layout";
import { Route as AppRoute } from "utilities/app-routes";

export const ApprovalQueue = () => {
  const location = useLocation();

  const currentUser = useCurrentUser();
  const client = currentUser.client;

  const { data: batchStatuses = [] } = useGetBatchStatusesQuery();
  const [search, setSearch] = useState<string>("");

  const [pageIndex, setPageIndex] = useState(0);
  const [{ pageSize = 25 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "dueDate", desc: false },
  ]);

  const handleSearchChange = useCallback((term: string) => {
    setSearch(term);
    setPageIndex(0);
  }, []);

  const sort = useMemo(
    () => sortingParams.map((param) => `${param.id},${param.desc ? "desc" : "asc"}`).join(","),
    [sortingParams],
  );

  const { data: batchPage, isLoading } = useBatches({
    clientId: client?.id,
    size: pageSize,
    page: pageIndex,
    sort,
    search,
    statusEquals: "IN_REVIEW",
  });

  const batches = useMemo(() => batchPage?.items ?? [], [batchPage?.items]);

  const pagesAmount = batchPage?.totalPages ?? 0;
  const totalRecords = batchPage?.totalItems ?? 0;

  const allColumns = useMemo(
    () =>
      createClientApprovalQueueColumns({
        pathname: location.pathname,
        batchStatuses,
      }),
    [location.pathname, batchStatuses],
  );

  const { tableColumnsConfig, columnOptions, visibleColumns, handleToggleColumn } =
    useClientTableColumnLayout({
      tableConfigKey: CLIENT_TABLE_CONFIG_KEYS.approvalQueue,
      allColumns,
      primaryColumnIds: ["name"],
    });

  const isSearchOrFiltering = Boolean(search);

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden bg-gray-100">
      <div className="shrink-0 pt-3 pl-3 pr-5 pb-4">
        <div className="mb-4 flex h-5 items-center">
          <Breadcrumbs
            breadcrumbs={[{ label: "Reviews", href: AppRoute.reviews }, { label: "Approval queue" }]}
          />
        </div>
        <TableSearchAndFilters
          setCurrentSearchTerm={handleSearchChange}
          placeholder={CLIENT_APPROVAL_QUEUE_SEARCH_PLACEHOLDER}
          tableFilters={
            <ColumnOptions
              tableConfigKey={CLIENT_TABLE_CONFIG_KEYS.approvalQueue}
              tableColumnsConfig={tableColumnsConfig}
              columnOptions={columnOptions}
              onToggleColumn={handleToggleColumn}
            />
          }
        />
      </div>

      <div className="min-h-0 flex-1 w-full overflow-y-auto custom-scroll">
        <div className="pl-3 pr-5 pb-5">
          <TableContainer className="mb-0">
            <TableLoader isLoading={isLoading} fullScreen />

            {!isLoading &&
              (batches.length ? (
                <DataGrid
                  columns={visibleColumns}
                  data={batches}
                  isLoading={false}
                  manualSorting={false}
                  showingRecordsIsOn={true}
                  viewByAmountSelectIsOn={true}
                  totalRecords={totalRecords}
                  sortingParams={sortingParams}
                  setSortingParams={setSortingParams}
                  pageSize={pageSize}
                  pagesAmount={pagesAmount}
                  setAmount={setAmount}
                  pageIndex={pageIndex}
                  setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                />
              ) : isSearchOrFiltering ? (
                <p className="text-gray-500">No results found</p>
              ) : (
                <p className="text-gray-500">There are no batches yet</p>
              ))}
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default ApprovalQueue;
